import React, { FC, useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import notification from "antd/es/notification";
import { useIntl } from "react-intl";

import Resource from "@mapmycustomers/shared/types/entity/Resource";
import { isNotEmpty } from "@mapmycustomers/shared/util/assert";
import { isEmptyString } from "@mapmycustomers/shared/util/stringUtils";
import {
  addSchemeToUrlIfEmpty,
  isValidUrl,
  removeSchemeFromUrl,
} from "@mapmycustomers/shared/util/url";
import { Modal, TextField } from "@mapmycustomers/ui";

import { updateResource } from "@app/scene/resources/store/actions";
import useStateWithTransformer from "@app/util/hook/useStateWithTransformer";

import styles from "./ActionsButton.module.scss";
import messages from "./messages";

export interface RenameModalProps {
  onHide: () => void;
  onUpdateResource: typeof updateResource.request;
  open: boolean;
  resource: Resource;
}

const RenameModal: FC<RenameModalProps> = ({ onHide, onUpdateResource, open, resource }) => {
  const intl = useIntl();

  const [newName, setNewName] = useState(resource.name);
  const [url, setUrl] = useStateWithTransformer<string>(
    removeSchemeFromUrl(resource.url ?? ""),
    removeSchemeFromUrl
  );
  const [urlError, setUrlError] = useState<string | undefined>();

  const isLink = isNotEmpty(resource.url); // We can assume if url is present then its a link type resource

  const handleRename = useCallback(() => {
    const urlWithScheme = addSchemeToUrlIfEmpty(url);
    if (isLink && !isValidUrl(urlWithScheme)) {
      setUrlError(intl.formatMessage(messages.invalidUrl));
    } else {
      setUrlError(undefined);
      onUpdateResource({
        resource: {
          id: resource.id,
          name: newName,
          url: isLink ? urlWithScheme : null,
        },
        successCallback: () => {
          notification.success({
            message: intl.formatMessage(isLink ? messages.updated : messages.renamed),
          });
          onHide();
        },
      });
    }
  }, [url, onUpdateResource, resource.id, newName, isLink, intl, onHide]);

  const handleCancel = useCallback(() => {
    setNewName(resource.name);
    onHide();
  }, [onHide, setNewName, resource.name]);

  useEffect(() => {
    setNewName(resource.name);
  }, [resource.name]);

  useEffect(() => {
    setUrl(removeSchemeFromUrl(resource.url ?? ""));
  }, [resource.url, setUrl]);

  return (
    <Modal
      okButtonProps={{
        disabled:
          (newName === resource.name && (!isLink || addSchemeToUrlIfEmpty(url) === resource.url)) ||
          isEmptyString(newName) ||
          (isLink && isEmptyString(url)),
      }}
      okText={intl.formatMessage(messages.apply)}
      onCancel={handleCancel}
      onOk={handleRename}
      open={open}
      title={intl.formatMessage(isLink ? messages.editLinkTitle : messages.renameTitle, {
        name: resource.name,
      })}
    >
      <div className={styles.renameModal}>
        <TextField
          label={intl.formatMessage(messages.renameLabel)}
          onChange={setNewName}
          required
          value={newName}
        />
        {isLink && (
          <TextField
            addonBefore="https://"
            error={urlError}
            label={intl.formatMessage(messages.url)}
            onChange={setUrl}
            required
            value={url}
          />
        )}
      </div>
    </Modal>
  );
};

const mapDispatchToProps = {
  onUpdateResource: updateResource.request,
};

export default connect(null, mapDispatchToProps)(RenameModal);
